import { useEffect, useRef, useState } from 'react';
import toast from '../../../../shared/Toast';

export interface Area {
    x: number; // x/y are the coordinates of the top/left corner of the cropped area
    y: number;
    width: number; // width of the cropped area
    height: number; // height of the cropped area
}

export const usePhotoEditor = (photo?: File) => {
    const [croppedPhoto, setCroppedPhoto] = useState<File | undefined>();
    const dimensions = useRef<Area>();

    const previewUrl = useRef('');
    const originalUrl = useRef('');

    const handleEdit = async () => {
        if (!photo || !dimensions.current) {
            throw new Error('No photo or crop dimensions available');
        }

        const croppedPhoto = await crop(photo, dimensions.current);

        if (previewUrl.current && previewUrl.current !== originalUrl.current) {
            URL.revokeObjectURL(previewUrl.current);
        }
        previewUrl.current = URL.createObjectURL(croppedPhoto);
        setCroppedPhoto(croppedPhoto);

        return croppedPhoto;
    };

    const handleCrop = async (newArea: Area) => {
        dimensions.current = newArea;
    };

    const crop = async (photo: File, dimensions?: Area) => {
        if (!dimensions) return photo;

        return new Promise<File>((resolve, reject) => {
            const image = new Image();
            const objectUrl = URL.createObjectURL(photo);
            image.src = objectUrl;

            image.onload = () => {
                URL.revokeObjectURL(objectUrl);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    toast.error('Error updating profile picture.');
                    return;
                }

                canvas.width = dimensions.width;
                canvas.height = dimensions.height;

                ctx.drawImage(
                    image,
                    dimensions.x,
                    dimensions.y,
                    dimensions.width,
                    dimensions.height,
                    0,
                    0,
                    dimensions.width,
                    dimensions.height,
                );
                canvas.toBlob((blob) => {
                    if (!blob)
                        return reject(new Error('Failed to process upload'));
                    resolve(
                        new File([blob], `cropped-${photo.name}`, {
                            type: blob.type,
                        }),
                    );
                }, photo.type);
            };

            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Failed to load image'));
            };
        });
    };

    useEffect(() => {
        if (!photo) return;

        const objectUrl = URL.createObjectURL(photo);
        previewUrl.current = objectUrl;
        originalUrl.current = objectUrl;
        setCroppedPhoto(photo);

        return () => {
            URL.revokeObjectURL(objectUrl);
            previewUrl.current = '';
            originalUrl.current = '';
            setCroppedPhoto(undefined);
        };
    }, [photo]);

    return {
        croppedPhoto,
        previewUrl,
        originalUrl,
        handleCrop,
        handleEdit,
    };
};
